'use client'

import { Badge, Cell, Image, Label, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ComplaintOrderItemModel } from '@marketplace-web/complaints/complaints-data'

type Props = {
  complaintOrderItems: Array<ComplaintOrderItemModel>
  isBuyerSide?: boolean
}

const ComplaintOrderItems = ({ complaintOrderItems, isBuyerSide = true }: Props) => {
  const translateSuffix = isBuyerSide ? 'buyer' : 'seller'

  const translate = useTranslate(
    `conversation.actions.request_return_modal.complaint_order_items.${translateSuffix}`,
  )

  const returnableItems = complaintOrderItems.filter(({ returnable }) => returnable)
  const nonReturnableItems = complaintOrderItems.filter(({ returnable }) => !returnable)

  const renderPhoto = (image: string | null) => {
    if (!image) return null

    return (
      <Image
        src={image}
        styling={Image.Styling.Rounded}
        size={Image.Size.Large}
        scaling={Image.Scaling.Cover}
      />
    )
  }

  const renderOrderItem = (orderItem: ComplaintOrderItemModel) => (
    <Cell
      key={orderItem.orderItemId}
      prefix={renderPhoto(orderItem.item.thumbnail)}
      title={
        <Text
          as="h2"
          text={<div className="u-text-wrap">{orderItem.item.title}</div>}
          type={Text.Type.Title}
        />
      }
      body={
        <>
          <Spacer size={Spacer.Size.Small} />
          <Badge content={orderItem.reasonText} theme="warning" styling={Badge.Styling.Light} />
        </>
      }
    />
  )

  const renderItemsList = ({
    labelKey,
    list,
  }: {
    labelKey: string
    list: Array<ComplaintOrderItemModel>
  }) => (
    <>
      <Label
        type={Label.Type.Stacked}
        text={<Text as="h3" type={Text.Type.Subtitle} text={translate(labelKey)} />}
      />
      {list.map(item => renderOrderItem(item))}
    </>
  )

  const renderReturnableItems = () => {
    if (!returnableItems.length) return null

    return renderItemsList({ list: returnableItems, labelKey: 'returnable' })
  }

  const renderNonReturnableItems = () => {
    if (!nonReturnableItems.length) return null

    return renderItemsList({ list: nonReturnableItems, labelKey: 'non_returnable' })
  }

  if (!complaintOrderItems.length) return null

  return (
    <>
      {renderReturnableItems()}
      {renderNonReturnableItems()}
    </>
  )
}

export default ComplaintOrderItems
